<template>
  <div class="px-4 pb-4">
    <template v-if="!refused">
      <v-divider class="mb-3" />
      <div class="text-body-2 primary-grey--text mb-2">
        {{ text('prompt') }}
      </div>
    </template>
    <v-btn
      depressed
      rounded
      :outlined="!refused"
      :color="COLORS.grainfoxAIButton"
      class="text-none text-body-2 font-weight-bold"
      :class="{ 'white--text': refused }"
      :loading="isStarting"
      @click="handleClick"
    >
      <v-icon
        small
        left
      >
        mdi-message-outline
      </v-icon>
      {{ text('button') }}
    </v-btn>
    <div
      v-if="refused"
      class="text-caption grey--text text--darken-1 mt-2"
    >
      {{ text('hint') }}
    </div>

    <v-dialog
      v-model="dialog"
      max-width="560"
      persistent
    >
      <v-card>
        <v-card-title class="text-h6">
          {{ advisorAssigned ? 'Send this to your advisor?' : 'Talk to an advisor' }}
        </v-card-title>

        <v-card-text
          v-if="!advisorAssigned"
          class="primary-grey--text"
        >
          We couldn't find your advisor. Contact our support team and we'll get you connected.
        </v-card-text>

        <v-card-text
          v-else
          class="primary-grey--text"
        >
          <p class="mb-3">
            We'll send {{ advisorName }} the following and open a chat with them.
          </p>
          <div
            v-for="field in preview"
            :key="field.label"
            class="mb-3"
          >
            <div class="text-caption grey--text text--darken-1">
              {{ field.label }}
            </div>
            <div class="text-body-2 text-pre-wrap">
              {{ field.value }}
            </div>
          </div>
          <p class="text-caption grey--text text--darken-1 mt-4 mb-0">
            GrainFox passes on only what is shown above.
          </p>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            class="text-none"
            :disabled="isBusy"
            @click="handleCancel"
          >
            {{ advisorAssigned ? 'Cancel' : 'Close' }}
          </v-btn>
          <v-btn
            v-if="!advisorAssigned"
            color="primary"
            depressed
            class="text-none"
            :href="supportUrl"
            target="_blank"
            rel="noopener"
          >
            Contact support
          </v-btn>
          <v-btn
            v-else
            color="primary"
            depressed
            class="text-none"
            :loading="isBusy"
            @click="handleSend"
          >
            Send
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { COLORS } from '@/helpers/colors';

// Shown when advisor_contact has no text from Sinoa (rules-admin), e.g. answers
// saved before data-platform sent it.
const DEFAULT_TEXT = {
  button: 'Message your advisor',
  hint: 'Or ask me about market prices and outlook.',
  prompt: 'Want to use this for your farm?',
};

// Where 'Contact support' sends users whose advisor could not be found (every
// customer should have one). Placeholder: the destination is not decided yet.
const SUPPORT_URL = 'mailto:support@grainfox.ca';

export default {
  name: 'SinoaAdvisorContact',

  props: {
    message: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      dialog: false,
      // False shows 'Contact support' (no advisor found: bad data, since every
      // customer has one).
      advisorAssigned: false,
      advisorName: null,
      preview: [],
      isStarting: false,
      isBusy: false,
      supportUrl: SUPPORT_URL,
      COLORS,
    };
  },

  computed: {
    refused() {
      return !!this.message.advisor_contact?.refused;
    },
  },

  mounted() {
    this.logAdvisorContactEvent({
      interactionId: this.message.id,
      event: 'advisor_button_displayed',
    });
  },

  methods: {
    ...mapActions('chatbot', [
      'logAdvisorContactEvent',
      'startAdvisorContact',
      'sendAdvisorContact',
    ]),

    async handleClick() {
      this.isStarting = true;
      try {
        const details = await this.startAdvisorContact(this.message.id);
        this.advisorAssigned = details.advisor_assigned;
        this.advisorName = details.advisor_name;
        this.preview = details.preview || [];
        this.dialog = true;
      } catch (error) {
        this.$snackbar?.error("We couldn't look up your advisor. Please try again.");
        this.logAdvisorContactEvent({
          interactionId: this.message.id,
          event: 'advisor_button_clicked',
          outcome: 'error',
          reason: 'lookup_failed',
        });
      } finally {
        this.isStarting = false;
      }
    },

    text(key) {
      return this.message.advisor_contact?.[key] || DEFAULT_TEXT[key];
    },

    handleCancel() {
      // Closing the support dialog is not a separate outcome: the server already
      // logged error / no_advisor.
      if (this.advisorAssigned) {
        this.logAdvisorContactEvent({
          interactionId: this.message.id,
          event: 'advisor_button_clicked',
          outcome: 'cancelled',
        });
      }
      this.dialog = false;
    },

    // The server logs chat_opened / error for send, so failures here only need
    // a message.
    async handleSend() {
      this.isBusy = true;
      try {
        const result = await this.sendAdvisorContact(this.message.id);
        window.location.href = `/messenger/${result.conversation_id}/`;
      } catch (error) {
        this.$snackbar?.error(error?.message || "We couldn't reach your advisor. Please try again.");
        this.dialog = false;
        this.isBusy = false;
      }
    },
  },
};
</script>
